import { FirebaseApp } from "firebase/app";
import { getFirestore, collection, query, where, documentId, getCountFromServer } from "firebase/firestore";
import { CurrentUser } from "../models/CurrentUser";

declare global {
    interface Window {
        isTest?: boolean;
    }
}

export class WhiteGuard {
    static async check(user: CurrentUser, fireApp: FirebaseApp): Promise<boolean> {
        // Skip whitelist check in test mode
        if (window.isTest) return true;
        
        const isDev = this.isDevelopmentEnvironment(fireApp);
        
        if (isDev) {
            try {
                const db = getFirestore(fireApp);

                if (user.firebaseUser && user.firebaseUser.email) {
                    const q = query(
                        collection(db, 'config'),
                        where(documentId(), '==', 'whitelistDEV'),
                        where('emails', 'array-contains', user.firebaseUser.email)
                    );

                    const snapshot = await getCountFromServer(q);

                    if (snapshot.data().count > 0) {
                        return true;
                    }
                    else{
                        this.showNoAccess(true);
                        return false;
                    }
                }
                return false;
            } catch (error) {
                console.error("Error checking whitelist:", error);
                // Fail safe: block access if check fails in dev
                return false;
            }
        }

        // For production environment: Allow all authenticated users
        //if (!isDev) return true;

        // For development environment: Strictly enforce whitelist
        try {
            const db = getFirestore(fireApp);
            
            if (user.firebaseUser && user.firebaseUser.email) {
                const q = query(
                    collection(db, 'config'),
                    where(documentId(), '==', 'whitelistOldUi'),
                    where('emails', 'array-contains', user.firebaseUser.email)
                );
                
                const snapshot = await getCountFromServer(q);
                
                if (snapshot.data().count > 0) {
                    return true;
                }
                else {
                    window.location.href = 'https://assignments.zoi.tech/newui#/';
                }
            }
        } catch (error) {
            console.error("Error checking whitelist:", error);
            // Fail safe: block access if check fails in dev
            return false;
        }

        return false;
    }

    static isDevelopmentEnvironment(fireApp: FirebaseApp): boolean {
        if (!fireApp) return false;
        const projectId = fireApp.options.projectId;
        return projectId === 'project-assignments';
    }

    static showNoAccess(isDev: boolean) {
        const productionUrl = 'https://assignments.zoi.tech/newui#/';
        
        const html = `
            <div style="
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                background-color: #f5f5f5;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif;
            ">
                <div style="
                    background: white;
                    padding: 48px 32px;
                    border-radius: 2px;
                    text-align: center;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02);
                ">
                    <div style="font-size: 72px; margin-bottom: 24px;">
                        ${isDev ? '<span style="color: #faad14">⚠️</span>' : '<span style="color: #ff4d4f">❗</span>'}
                    </div>
                    
                    <h2 style="margin: 0 0 16px 0; color: rgba(0, 0, 0, 0.88); font-weight: 600; font-size: 24px;">
                        ${isDev ? "Development Environment - Access Restricted" : "Access Restricted"}
                    </h2>
                    
                    <p style="margin: 0 0 24px 0; color: rgba(0, 0, 0, 0.45); font-size: 14px;">
                        ${isDev 
                            ? "This is a development environment restricted to developers and team members only"
                            : "You don't have permission to access this application"
                        }
                    </p>

                    ${isDev ? `
                        <div style="
                            background-color: #fffbe6;
                            border: 1px solid #ffe58f;
                            padding: 16px;
                            border-radius: 2px;
                            text-align: left;
                            margin-bottom: 24px;
                        ">
                            <div style="display: flex; gap: 8px;">
                                <span style="color: #faad14">⚠️</span>
                                <div>
                                    <div style="font-weight: 500; margin-bottom: 4px; color: rgba(0, 0, 0, 0.88);">Important Notice</div>
                                    <div style="color: rgba(0, 0, 0, 0.88); font-size: 14px;">
                                        You are attempting to access the <strong>Development Environment</strong>. 
                                        This environment is for testing purposes only and contains test data that may not reflect real projects or demands.
                                    </div>
                                </div>
                            </div>
                        </div>
                    ` : ''}

                    <div style="color: rgba(0, 0, 0, 0.45); font-size: 14px; margin-bottom: 24px; line-height: 1.5;">
                        ${isDev 
                            ? "Access to the development environment is restricted to prevent confusion with production data and to ensure only authorized personnel work with test demands."
                            : "This application is currently in beta testing phase and access is restricted to authorized personnel only."
                        }
                    </div>

                    ${isDev ? `
                        <div style="margin-bottom: 24px;">
                            <div style="font-weight: 600; margin-bottom: 12px; color: rgba(0, 0, 0, 0.88);">Looking for the production environment?</div>
                            <a href="${productionUrl}" style="
                                display: inline-block;
                                background: #1677ff;
                                color: white;
                                text-decoration: none;
                                padding: 8px 15px;
                                border-radius: 6px;
                                font-size: 16px;
                                transition: all 0.2s;
                                box-shadow: 0 2px 0 rgba(5, 145, 255, 0.1);
                            ">
                                Go to Production Environment
                            </a>
                        </div>
                    ` : ''}

                    <div style="color: rgba(0, 0, 0, 0.45); font-size: 14px;">
                        ${isDev 
                            ? "If you're a developer or team member and need access, please contact the Development Team."
                            : "If you believe you should have access to this tool, please contact your Lead or the Development Team."
                        }
                    </div>
                </div>
            </div>
        `;

        document.body.innerHTML = html;
        // Stop any further execution/rendering if possible, though replacing body does a good job visually.
    }
}
